/**
 * Screen Mirroring capability — distinct from Cast-to-Device.
 *
 * Correct direction: DEVICE → EXTERNAL DISPLAY (OS Miracast / wireless display /
 * AirPlay Screen Mirroring), e.g. Windows PC → wireless-display UI → Roku TV.
 *
 * Absolute rules:
 * - Never invent Cast devices or claim Roku/Fire/Samsung/LG discovery.
 * - Never call getDisplayMedia() — Pentecost+ must not capture the user's
 *   screen into the browser ("sharing your screen" / browser screen share).
 * - Never synthesize Win+K keypresses or fake OS/Roku dialogs.
 * - Prefer legitimate OS URI / intent handoff on user gesture when the platform
 *   exposes one; otherwise a single short fallback line (never a help article).
 * - Optional native shell bridge (`window.__TPG_NATIVE_SCREEN_MIRROR__`) may
 *   open OS controls when a branded app exists — still not browser capture.
 * - "Stop Screen Mirror" re-opens the OS connect/disconnect UI; it does not
 *   claim Pentecost+ terminated the OS session.
 */

import { createEmitter } from "../../Utilities/events";

export var ScreenMirrorMode = Object.freeze({
  OS_HANDOFF: "os_handoff",
  NATIVE_BRIDGE: "native_bridge",
  UNAVAILABLE: "unavailable",
});

/** Win+K Connect / wireless-display flyout equivalent (not a Settings deep-link invent). */
export var WINDOWS_WIRELESS_DISPLAY_URI =
  "ms-settings-connectabledevices:devicediscovery";

/** Android system Cast / wireless display settings (Chrome intent URI). */
export var ANDROID_CAST_SETTINGS_INTENT =
  "intent:#Intent;action=android.settings.CAST_SETTINGS;end";

/**
 * @param {object} [env]
 * @returns {{ osFamily: string, browser: string, ua: string }}
 */
export function detectHostEnvironment(env) {
  env = env || {};
  var nav = env.navigator || (typeof navigator !== "undefined" ? navigator : null);
  var ua = (nav && nav.userAgent) || "";
  var platform = (nav && (nav.userAgentData && nav.userAgentData.platform)) ||
    (nav && nav.platform) ||
    "";

  var osFamily = "unknown";
  if (/Android/i.test(ua)) osFamily = "android";
  else if (/iPhone|iPad|iPod/i.test(ua)) osFamily = "ios";
  else if (
    /Macintosh/i.test(ua) &&
    nav &&
    typeof nav.maxTouchPoints === "number" &&
    nav.maxTouchPoints > 1
  ) {
    osFamily = "ios";
  } else if (/Mac/i.test(platform) || /Macintosh/i.test(ua)) osFamily = "macos";
  else if (/Win/i.test(platform) || /Windows/i.test(ua)) osFamily = "windows";
  else if (/Linux/i.test(platform) || /Linux/i.test(ua)) osFamily = "linux";

  var browser = "other";
  if (/Edg\//i.test(ua)) browser = "edge";
  else if (/Chrome|CriOS/i.test(ua) && !/Edg\//i.test(ua)) browser = "chrome";
  else if (/Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Firefox/i.test(ua))
    browser = "safari";
  else if (/Firefox|FxiOS/i.test(ua)) browser = "firefox";

  return { osFamily: osFamily, browser: browser, ua: ua, platform: platform };
}

/**
 * Platform handoff target for Screen Mirror start/stop (same UI for connect & disconnect).
 * @param {string} osFamily
 * @returns {{ uri: string|null, canAttemptLaunch: boolean, fallbackHint: string|null }}
 */
export function resolveOsHandoffTarget(osFamily) {
  if (osFamily === "windows") {
    return {
      uri: WINDOWS_WIRELESS_DISPLAY_URI,
      canAttemptLaunch: true,
      // Only used if the browser blocks the protocol navigation.
      fallbackHint: "Press Win + K to connect or disconnect a wireless display.",
    };
  }
  if (osFamily === "android") {
    return {
      uri: ANDROID_CAST_SETTINGS_INTENT,
      canAttemptLaunch: true,
      fallbackHint: "Open Cast / Screen Mirroring in Quick Settings.",
    };
  }
  if (osFamily === "ios" || osFamily === "macos") {
    return {
      uri: null,
      canAttemptLaunch: false,
      fallbackHint: "Open Control Center → Screen Mirroring.",
    };
  }
  return {
    uri: null,
    canAttemptLaunch: false,
    fallbackHint: "Use your device's built-in screen mirroring controls.",
  };
}

/**
 * Attempt to open a registered OS URI / Android intent on a user gesture.
 * Does not synthesize keypresses. Returns whether navigation was dispatched —
 * browsers may still block custom protocols; callers must not claim success beyond that.
 *
 * @param {string} uri
 * @param {object} [env] injectable { document, location }
 * @returns {{ launched: boolean, uri: string, method: string|null }}
 */
export function attemptOsUriHandoff(uri, env) {
  env = env || {};
  if (!uri) {
    return { launched: false, uri: null, method: null };
  }

  var doc =
    env.document || (typeof document !== "undefined" ? document : null);
  var loc =
    env.location ||
    (typeof window !== "undefined" && window.location
      ? window.location
      : null);

  if (doc && doc.body && typeof doc.createElement === "function") {
    try {
      var a = doc.createElement("a");
      a.href = uri;
      a.rel = "noopener";
      a.style.display = "none";
      doc.body.appendChild(a);
      a.click();
      if (a.parentNode) a.parentNode.removeChild(a);
      return { launched: true, uri: uri, method: "anchor_click" };
    } catch (e) {
      /* fall through */
    }
  }

  if (loc && typeof loc.assign === "function") {
    try {
      loc.assign(uri);
      return { launched: true, uri: uri, method: "location_assign" };
    } catch (e2) {
      /* fall through */
    }
  }

  return { launched: false, uri: uri, method: null };
}

/**
 * Detect what Screen Mirror can honestly do in this environment.
 * @param {object} [env] injectable for tests
 * @returns {object}
 */
export function detectScreenMirrorEnvironment(env) {
  env = env || {};
  var host = detectHostEnvironment(env);

  var nativeBridge =
    env.nativeBridge ||
    (typeof window !== "undefined" && window.__TPG_NATIVE_SCREEN_MIRROR__) ||
    null;
  var hasNativeBridge =
    !!(nativeBridge && typeof nativeBridge.startScreenMirror === "function");

  var osWirelessDisplay = null;
  var rokuPathway = "none";
  if (host.osFamily === "windows") {
    osWirelessDisplay = "miracast";
    rokuPathway = "os_miracast";
  } else if (host.osFamily === "android") {
    osWirelessDisplay = "android_wireless_display";
    rokuPathway = "os_miracast";
  } else if (host.osFamily === "ios" || host.osFamily === "macos") {
    osWirelessDisplay = "airplay_screen_mirroring";
    rokuPathway = "os_airplay";
  }

  var handoff = resolveOsHandoffTarget(host.osFamily);

  var programmaticStart = hasNativeBridge
    ? ScreenMirrorMode.NATIVE_BRIDGE
    : ScreenMirrorMode.OS_HANDOFF;

  return {
    kind: "screen_mirror",
    distinctFromCast: true,
    neverCapturesIntoPentecost: true,
    osFamily: host.osFamily,
    browser: host.browser,
    hasNativeBridge: hasNativeBridge,
    programmaticStart: programmaticStart,
    osWirelessDisplay: osWirelessDisplay,
    rokuPathway: rokuPathway,
    alwaysOfferInMenu: true,
    // True when we can *attempt* a legitimate OS URI/intent (not Win+K synthesis).
    canAttemptOsUriHandoff: !hasNativeBridge && handoff.canAttemptLaunch,
    canLaunchOsWirelessDisplaySelector: !hasNativeBridge && handoff.canAttemptLaunch,
    handoffUri: handoff.uri,
    fallbackHint: handoff.fallbackHint,
    osOwnsSession: true,
  };
}

/**
 * Minimal platform hint only — not a multi-OS help article.
 * Kept for CastingProvider re-export compatibility.
 * @param {object} info
 * @returns {{ title: string, summary: string, steps: string[], rokuNote: string, castVsMirror: string }}
 */
export function buildScreenMirrorInstructions(info) {
  info = info || {};
  var handoff = resolveOsHandoffTarget(info.osFamily || "unknown");
  var hint = handoff.fallbackHint || "";
  return {
    title: "Screen Mirror",
    summary: hint,
    steps: hint ? [hint] : [],
    rokuNote:
      "Pentecost+ does not control the system-level mirroring connection.",
    castVsMirror:
      "Cast to Device sends Pentecost+ playback to a discovered Cast receiver. Screen Mirror uses your device's built-in mirroring — Pentecost+ does not control the system-level mirroring connection.",
  };
}

/**
 * Create Screen Mirror capability object (reusable for Android/iOS shells).
 * Not a Cast device provider — never appears in cast device discovery lists.
 *
 * @param {object} [options]
 */
export function createScreenMirrorProvider(options) {
  options = options || {};
  var emitter = createEmitter();
  var detection =
    options.detection ||
    detectScreenMirrorEnvironment(options.env || {});
  var lastResult = null;
  var active = false;

  function snapshot() {
    return {
      kind: "screen_mirror",
      active: active,
      detection: detection,
      lastResult: lastResult,
    };
  }

  function emitChange() {
    emitter.emit("screenMirror", snapshot());
  }

  function runOsHandoff(intent) {
    detection = detectScreenMirrorEnvironment(options.env || {});
    var handoff = resolveOsHandoffTarget(detection.osFamily);
    var attempt = handoff.uri
      ? attemptOsUriHandoff(handoff.uri, options.env || {})
      : { launched: false, uri: null, method: null };

    lastResult = {
      ok: true,
      mode: ScreenMirrorMode.OS_HANDOFF,
      intent: intent || "start",
      distinctFromCast: true,
      neverCapturesIntoPentecost: true,
      osOwnsSession: true,
      launched: !!attempt.launched,
      uri: attempt.uri || handoff.uri,
      method: attempt.method,
      // Never claim Pentecost+ connected/disconnected the OS session.
      note: attempt.launched
        ? intent === "stop"
          ? "Opened the OS wireless-display UI so you can disconnect. Pentecost+ does not terminate the OS mirroring session."
          : "Opened the OS wireless-display UI. Complete connect in the system dialog."
        : null,
      fallbackHint: attempt.launched ? null : handoff.fallbackHint,
    };
    return lastResult;
  }

  return {
    name: "screen_mirror",
    kind: "screen_mirror",

    getDetection: function () {
      return detection;
    },

    refreshDetection: function (env) {
      detection = detectScreenMirrorEnvironment(env || options.env || {});
      emitChange();
      return detection;
    },

    getSnapshot: snapshot,

    isMenuOptionAlwaysVisible: function () {
      return true;
    },

    isActive: function () {
      return active;
    },

    getLastResult: function () {
      return lastResult;
    },

    /**
     * Start Screen Mirror pathway.
     * Prefer native bridge → OS URI/intent handoff → minimal fallback hint.
     * Never calls getDisplayMedia. Never invents devices. Never synthesizes Win+K.
     */
    start: function () {
      detection = detectScreenMirrorEnvironment(options.env || {});

      if (detection.programmaticStart === ScreenMirrorMode.NATIVE_BRIDGE) {
        var bridge =
          (options.env && options.env.nativeBridge) ||
          (typeof window !== "undefined" &&
            window.__TPG_NATIVE_SCREEN_MIRROR__);
        return Promise.resolve(bridge.startScreenMirror())
          .then(function (nativeResult) {
            active = true;
            lastResult = {
              ok: true,
              mode: ScreenMirrorMode.NATIVE_BRIDGE,
              intent: "start",
              distinctFromCast: true,
              neverCapturesIntoPentecost: true,
              osOwnsSession: true,
              launched: true,
              nativeResult: nativeResult || null,
              fallbackHint: null,
            };
            emitChange();
            return lastResult;
          })
          .catch(function (err) {
            lastResult = {
              ok: false,
              mode: ScreenMirrorMode.NATIVE_BRIDGE,
              intent: "start",
              distinctFromCast: true,
              neverCapturesIntoPentecost: true,
              osOwnsSession: true,
              launched: false,
              error: (err && err.message) || String(err),
              fallbackHint: detection.fallbackHint,
            };
            emitChange();
            return lastResult;
          });
      }

      var result = runOsHandoff("start");
      // Menu shows Stop after the user initiates handoff (local UI state only).
      active = true;
      emitChange();
      return Promise.resolve(result);
    },

    /**
     * Re-open OS connect/disconnect UI. Clears local menu active flag.
     * Does not claim Pentecost+ disconnected the OS mirroring session.
     */
    stop: function () {
      detection = detectScreenMirrorEnvironment(options.env || {});

      if (
        detection.programmaticStart === ScreenMirrorMode.NATIVE_BRIDGE &&
        typeof window !== "undefined" &&
        window.__TPG_NATIVE_SCREEN_MIRROR__ &&
        typeof window.__TPG_NATIVE_SCREEN_MIRROR__.stopScreenMirror === "function"
      ) {
        var bridge = window.__TPG_NATIVE_SCREEN_MIRROR__;
        return Promise.resolve(bridge.stopScreenMirror())
          .then(function (nativeResult) {
            active = false;
            lastResult = {
              ok: true,
              mode: "stopped",
              intent: "stop",
              distinctFromCast: true,
              neverCapturesIntoPentecost: true,
              osOwnsSession: true,
              launched: true,
              nativeResult: nativeResult || null,
              note:
                "Native bridge was asked to open disconnect. Pentecost+ does not claim the OS session ended.",
              fallbackHint: null,
            };
            emitChange();
            return lastResult;
          })
          .catch(function (err) {
            active = false;
            lastResult = {
              ok: false,
              mode: "stopped",
              intent: "stop",
              distinctFromCast: true,
              neverCapturesIntoPentecost: true,
              osOwnsSession: true,
              launched: false,
              error: (err && err.message) || String(err),
              fallbackHint: detection.fallbackHint,
            };
            emitChange();
            return lastResult;
          });
      }

      var result = runOsHandoff("stop");
      active = false;
      emitChange();
      return Promise.resolve(result);
    },

    on: function (type, fn) {
      return emitter.on(type, fn);
    },

    destroy: function () {
      active = false;
      emitter.removeAllListeners && emitter.removeAllListeners();
    },
  };
}

export default createScreenMirrorProvider;
